import React, { useState, useEffect } from 'react';
import {
  TrendingUp,
  Users,
  CreditCard,
  AlertCircle,
  Play,
  Filter,
  ExternalLink
} from 'lucide-react';
import './Analytics.css';

function PostHogAnalytics() {
  const [posthogUrl, setPosthogUrl] = useState('');

  useEffect(() => {
    // Get PostHog project URL from environment or settings
    const projectUrl = process.env.REACT_APP_POSTHOG_PROJECT_URL || 'https://app.posthog.com';
    setPosthogUrl(projectUrl);
  }, []);

  const funnels = [
    {
      id: 'registration',
      name: 'Воронка регистрации',
      icon: Users,
      color: 'blue',
      steps: [
        'Registration Started',
        'Phone Number Entered',
        'Code Verified',
        'Registration Completed'
      ],
      description: 'Отслеживание процесса регистрации пользователей'
    },
    {
      id: 'payment',
      name: 'Воронка оплаты',
      icon: CreditCard,
      color: 'green',
      steps: [
        'Viewed Subscription Plans',
        'Selected Plan',
        'Initiated Payment',
        'Payment Successful',
        'Subscription Activated'
      ],
      description: 'Отслеживание процесса оформления подписки'
    }
  ];

  const features = [
    {
      id: 'session-replay',
      name: 'Session Replay',
      icon: Play,
      description: 'Просмотр записей сессий пользователей для анализа UX',
      link: `${posthogUrl}/recordings`,
      color: 'purple'
    },
    {
      id: 'funnels',
      name: 'Воронки',
      icon: Filter,
      description: 'Анализ конверсии на каждом этапе пользовательского пути',
      link: `${posthogUrl}/insights`,
      color: 'orange'
    },
    {
      id: 'errors',
      name: 'Отслеживание ошибок',
      icon: AlertCircle,
      description: 'Мониторинг ошибок и проблем в приложении',
      link: `${posthogUrl}/events?event=error_occurred`,
      color: 'red'
    },
    {
      id: 'trends',
      name: 'Тренды',
      icon: TrendingUp,
      description: 'Анализ трендов использования функций',
      link: `${posthogUrl}/insights`,
      color: 'cyan'
    }
  ];

  const FunnelCard = ({ funnel }) => (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">
          <funnel.icon size={20} />
          {funnel.name}
        </h3>
      </div>
      <div className="card-content">
        <p className="text-secondary" style={{ marginBottom: '16px' }}>
          {funnel.description}
        </p>
        <div className="funnel-steps">
          {funnel.steps.map((step, index) => (
            <div key={index} className="funnel-step">
              <div className="funnel-step-number">{index + 1}</div>
              <div className="funnel-step-name">{step}</div>
            </div>
          ))}
        </div>
        <a
          href={`${posthogUrl}/insights?funnel=${funnel.id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-primary"
          style={{ marginTop: '16px', width: '100%' }}
        >
          <ExternalLink size={18} />
          Открыть в PostHog
        </a>
      </div>
    </div>
  );

  const FeatureCard = ({ feature }) => (
    <div className="analytics-stat-card clickable">
      <div className={`stat-icon ${feature.color}`}>
        <feature.icon size={24} />
      </div>
      <div className="stat-content">
        <span className="stat-title">{feature.name}</span>
        <p className="text-secondary" style={{ fontSize: '13px', marginTop: '4px' }}>
          {feature.description}
        </p>
        <a
          href={feature.link}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-outline"
          style={{ marginTop: '12px', fontSize: '13px', padding: '6px 12px' }}
        >
          <ExternalLink size={14} />
          Открыть
        </a>
      </div>
    </div>
  );

  return (
    <div className="analytics-page fade-in">
      <div className="page-header">
        <div>
          <h1 className="page-title">PostHog Analytics</h1>
          <p className="text-secondary" style={{ marginTop: '8px' }}>
            Продвинутая аналитика с воронками, session replay и отслеживанием ошибок
          </p>
        </div>
        <a
          href={posthogUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-primary"
        >
          <ExternalLink size={18} />
          Открыть PostHog Dashboard
        </a>
      </div>

      {/* Info Banner */}
      <div className="card" style={{ 
        background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(139, 92, 246, 0.1))',
        border: '1px solid rgba(99, 102, 241, 0.3)',
        marginBottom: '32px'
      }}>
        <div className="card-content">
          <h3 style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <AlertCircle size={20} />
            О PostHog
          </h3>
          <p className="text-secondary">
            PostHog - это платформа для продуктовой аналитики с открытым исходным кодом. 
            Она позволяет отслеживать поведение пользователей, анализировать воронки конверсии, 
            просматривать записи сессий и проводить A/B тесты.
          </p>
          <div style={{ marginTop: '16px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <a
              href="https://posthog.com/docs"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline"
              style={{ fontSize: '13px' }}
            >
              Документация
            </a>
            <a
              href="/POSTHOG_INTEGRATION.md"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline"
              style={{ fontSize: '13px' }}
            >
              Руководство по интеграции
            </a>
          </div>
        </div>
      </div>

      {/* Funnels Section */}
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{ marginBottom: '16px', fontSize: '20px' }}>Воронки конверсии</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px' }}>
          {funnels.map(funnel => (
            <FunnelCard key={funnel.id} funnel={funnel} />
          ))}
        </div>
      </div>

      {/* Features Section */}
      <div>
        <h2 style={{ marginBottom: '16px', fontSize: '20px' }}>Возможности PostHog</h2>
        <div className="analytics-stats">
          {features.map(feature => (
            <FeatureCard key={feature.id} feature={feature} />
          ))}
        </div>
      </div>

      {/* Quick Links */}
      <div className="card" style={{ marginTop: '32px' }}>
        <div className="card-header">
          <h3 className="card-title">Быстрые ссылки</h3>
        </div>
        <div className="card-content">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '12px' }}>
            <a
              href={`${posthogUrl}/recordings`}
              target="_blank"
              rel="noopener noreferrer"
              className="quick-link"
            >
              <Play size={16} />
              Session Recordings
            </a>
            <a
              href={`${posthogUrl}/insights`}
              target="_blank"
              rel="noopener noreferrer"
              className="quick-link"
            >
              <TrendingUp size={16} />
              Insights & Trends
            </a>
            <a
              href={`${posthogUrl}/events`}
              target="_blank"
              rel="noopener noreferrer"
              className="quick-link"
            >
              <Filter size={16} />
              Events Explorer
            </a>
            <a
              href={`${posthogUrl}/persons`}
              target="_blank"
              rel="noopener noreferrer"
              className="quick-link"
            >
              <Users size={16} />
              Users & Cohorts
            </a>
          </div>
        </div>
      </div>

      <style jsx>{`
        .funnel-steps {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .funnel-step {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px;
          background: var(--bg-card-hover);
          border-radius: 8px;
          border: 1px solid var(--border-primary);
        }

        .funnel-step-number {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: var(--accent-primary);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 14px;
          flex-shrink: 0;
        }

        .funnel-step-name {
          font-size: 14px;
          color: var(--text-primary);
        }

        .quick-link {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 16px;
          background: var(--bg-card-hover);
          border: 1px solid var(--border-primary);
          border-radius: 8px;
          color: var(--text-primary);
          text-decoration: none;
          transition: all 0.2s ease;
          font-size: 14px;
        }

        .quick-link:hover {
          border-color: var(--accent-primary);
          background: var(--bg-card);
          transform: translateY(-2px);
        }
      `}</style>
    </div>
  );
}

export default PostHogAnalytics;
